import React, { FC, useState } from "react";
import styled from "styled-components";
import VideoModal from "./VideoModal";
import { button } from "../lib/sharedStyles";
import SvgPlay from "./Icons/Play";
import { Asset, Entry } from "contentful";
import { useModal } from "../contexts/ModalContext";
import { marked } from "marked";

export interface VideoBlockProps extends Entry {
  id: string;
  brand: string;
  fields: {
    ctaLabel?: string;
    mobileThumbnail?: Asset;
    textContent?: string;
    thumbnail: Asset;
    videoUrl: string;
    showInline?: boolean;
  },
  parentProps: {
    carouselTheme: string;
    parentComponentKey?: string;
  }
}

const VideoBox = styled.div<{ $allowHover: boolean; $theme: string; }>`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 10px;
  cursor: pointer;

  ${({ $allowHover }) => $allowHover && `
    &:hover {
      div {
        opacity: 1;
      }
    }
  `}

  ${({ $allowHover, $theme }) => $allowHover && $theme == 'light' && `
    &:hover {
      span {
        color: #fff;
        border-color: #333333;
        background-color: #333333;
      }
    }
  `}

  ${({ $allowHover, $theme }) => $allowHover && $theme == 'dark' && `
    &:hover {
      span {
        color: #000;
        border-color: #EAEAEA;
        background-color: #EAEAEA;
      }
    }
  `}
`;

const CardImage = styled.div<{ $hidden: boolean; }>`
  position: relative;
  height: auto;
  pointer-events: none;

  img, video {
    width: 100%;
  }

  ${({ $hidden }) => $hidden && `
    opacity: 0;
    pointer-events: none;
  `}
`;

const CaptionBox = styled.div<{ $inline: boolean; $hidden: boolean; $brand: string; }>`
  background-color: transparent;
  height: auto;
  max-width: 100%;
  position: relative;
  width: 100%;
  z-index: 1;
  padding: 16px;

  ${({ $hidden }) => $hidden && `
    opacity: 0;
    pointer-events: none;
  `}

  ${({ $inline }) => $inline && `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    p, h1, h2, h3 {
      color: #fff;
    }
  `}

  ${({$brand }) => $brand == 'jackson' && `
    h5 {
      color: #287104;
      font-family: Palanquin Dark, sans-serif;
      font-weight: 600;
      font-size: 14px;
      letter-spacing: .0625rem;
      margin-bottom: 0px;
      padding-bottom: 0;
      padding-top: 0;
      margin-top: -15px;
    }

    h2 {
      color: #fff;
      text-transform: uppercase;
      font-weight: 600;
      font-size: 1.2rem;
      letter-spacing: 1px;
      margin-right: 2.5625rem;
    }

    h3 {
      color: #fff;
      font-family: Palanquin Dark, sans-serif;
      font-weight: 500;
      font-size: 1.25rem;
      line-height: 1.5rem;
      text-align: left;
      margin-top: .5rem;
      max-width: 100%;
    }
  `}
`;

const Button = styled.span<{ $theme: string; $brand: string; }>`
  ${({ $theme, $brand }) => $theme == 'light' ? button('Black Stroke Pill', $brand) : button('White Stroke Pill', $brand)}
  font-size: 14px;
  min-width: auto;
  padding: 7px 20px;
  margin-top: 8px;
`;

const PlayIconWrap = styled.div<{ $inline: boolean; }>`
  background-color: #fff;
  border-radius: 100%;
  width: 45px;
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  ${({ $inline }) => !$inline && `
    opacity: 0;
    position: absolute;
    right: 16px;
    bottom: 16px;
  `}
`;

const ImageThumbnail = styled.img<{ $hide?: string; }>`
  ${({ $hide }) => $hide == 'mobile' && `
    display: none;

    @media (min-width: 768px) {
      display: block;
    }
  `}

  ${({ $hide }) => $hide == 'desktop' && `
    @media (min-width: 768px) {
      display: none;
    }
  `}
`;

const VideoThumbnail = styled.video<{ $hide?: string; }>`
  ${({ $hide }) => $hide == 'mobile' && `
    display: none;

    @media (min-width: 768px) {
      display: block;
    }
  `}

  ${({ $hide }) => $hide == 'desktop' && `
    @media (min-width: 768px) {
      display: none;
    }
  `}
`;

const Frame = styled.iframe`
  background: #000;
  box-shadow: 0 0 .5rem rgba(0, 0, 0, .6);
  display: block;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
`;

const Thumbnail = (asset: Asset, hide: string) => {
  const contentType = asset.fields.file?.contentType as string;

  if (contentType.includes('video')) {
    return (
      <VideoThumbnail autoPlay loop muted playsInline $hide={hide}>
        <source
          src={`https:${asset.fields.file?.url}`}
          type={contentType}
        />
      </VideoThumbnail> 
    );
  }

  return (
    <ImageThumbnail 
      $hide={hide}
      loading="lazy"
      src={`https:${asset.fields.file?.url}`} 
    />
  );
}

const VideoBlock: FC<VideoBlockProps> = ({ 
  id,
  brand,
  fields: {
    ctaLabel,
    mobileThumbnail,
    textContent,
    thumbnail,
    videoUrl,
    showInline = false,
  }, 
  parentProps: {
    carouselTheme,
    parentComponentKey,
  }
}) => {
  const { showModal, hideModal } = useModal();
  const [showInlineVideo, setShowInlineVideo]= useState(false);
  const isInline = (showInline && parentComponentKey != 'carousel');
  const triggerVideo = () => {
    if (isInline) {
      setShowInlineVideo(true);
    } else {
      showModal(VideoModal, { embedURL: videoUrl, close: hideModal });
    }
  }

  return (
    <>
      <VideoBox 
        data-component-id={id}
        onClick={triggerVideo} 
        $allowHover={!showInlineVideo} 
        $theme={carouselTheme}
      >
        {isInline && showInlineVideo && (
          <Frame 
            width="100%" 
            height="500" 
            src={videoUrl} 
            title="YouTube video player" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; gyroscope; picture-in-picture" 
            allowFullScreen
          ></Frame>
        )}
        <CardImage $hidden={showInlineVideo}>
          {Thumbnail(thumbnail, mobileThumbnail ? 'mobile' : '')}
          {mobileThumbnail && Thumbnail(mobileThumbnail, 'desktop')}
          {(!isInline) && (
            <PlayIconWrap $inline={isInline}>
              <SvgPlay fontSize="16px" />
            </PlayIconWrap>
          )}
        </CardImage>
        <CaptionBox $inline={isInline} $hidden={showInlineVideo} $brand={brand}>
          {(textContent) && (
            <div dangerouslySetInnerHTML={{__html: marked.parse(textContent)}} />
          )}
          {ctaLabel && !isInline && <Button $theme={carouselTheme} $brand={brand}>{ctaLabel}</Button>}

          {(isInline) && (
            <PlayIconWrap $inline={isInline}>
              <SvgPlay fontSize="16px" />
            </PlayIconWrap>            
          )}
        </CaptionBox>
      </VideoBox>
    </>
  );
};

export default VideoBlock;